import { Box, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from 'components/Common/Button';
import { BusinessAddon } from '@/apis/business/addons/types';
import { useReverseBusinessAddonCancellation } from '@/apis/business/addons/hook';
import { getAddonDisplayTitle } from './addonDisplay';

interface ReverseCancellationDialogProps {
  addon: BusinessAddon | null;
  open: boolean;
  onClose: () => void;
}

export default function ReverseCancellationDialog({ addon, open, onClose }: ReverseCancellationDialogProps) {
  const { mutateAsync: reverseCancellation, isPending } = useReverseBusinessAddonCancellation();

  if (!addon) return null;

  const title = getAddonDisplayTitle(addon);

  const handleConfirm = async () => {
    await reverseCancellation(addon.code);
    onClose();
  };

  return (
    <Dialog open={open} onClose={isPending ? undefined : onClose} maxWidth='xs' fullWidth>
      <DialogContent sx={{ pt: 3.5, px: 3, pb: 1.5 }}>
        <Stack alignItems='center' textAlign='center' mb={2.5}>
          <Box
            width={48}
            height={48}
            borderRadius='50%'
            bgcolor='#FEF3C7'
            display='flex'
            alignItems='center'
            justifyContent='center'
            mb={1.5}
          >
            <FiAlertTriangle size={24} color='#D97706' />
          </Box>
          <Typography fontSize={20} fontWeight={600} mb={1}>
            Reverse Cancellation?
          </Typography>
          <Typography fontSize={14} color='text.secondary' lineHeight={1.5}>
            Are you sure you want to reverse the cancellation of {title}?
          </Typography>
        </Stack>

        <Typography fontSize={14} fontWeight={500} mb={1}>
          What will happen next:
        </Typography>
        <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
          <Typography component='li' fontSize={14} color='text.secondary' lineHeight={1.5}>
            Your add-on will remain active and continue to renew.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
        <Button variant='outlined' onClick={onClose} disabled={isPending} sx={{ textTransform: 'none', minWidth: 96 }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleConfirm}
          disabled={isPending}
          isPending={isPending}
          loadingText='Confirming'
          sx={{ textTransform: 'none', minWidth: 96 }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
